import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import type { AuthSession, AuthState, LoginCredentials, RegisterCredentials, OAuthProvider } from '../types/auth';
import type { User } from '@aliza/shared';
import {
  login as authLogin,
  register as authRegister,
  loginWithOAuth as authLoginWithOAuth,
  logout as authLogout,
  getStoredSession,
  loginAsGuest as authLoginAsGuest,
  startAutoRefresh,
  stopAutoRefresh,
  clearStoredSession,
} from '../services/auth-service';

interface AuthContextValue {
  user: User | null;
  session: AuthSession | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (credentials: RegisterCredentials) => Promise<void>;
  loginWithOAuth: (provider: OAuthProvider, token: string) => Promise<void>;
  loginAsGuest: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    session: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    const stored = getStoredSession();
    if (stored && stored.expiresAt > 0 && stored.expiresAt * 1000 < Date.now()) {
      // Token is expired — clear it instead of pretending we're logged in
      clearStoredSession();
      setState({ session: null, isLoading: false, error: null });
    } else {
      setState({ session: stored, isLoading: false, error: null });
      if (stored) {
        startAutoRefresh();
      }
    }
    return () => stopAutoRefresh();
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setState((s) => ({ ...s, isLoading: true, error: null }));
    try {
      const session = await authLogin(credentials);
      setState({ session, isLoading: false, error: null });
      startAutoRefresh();
    } catch (err: unknown) {
      setState((s) => ({ ...s, isLoading: false, error: err instanceof Error ? err.message : 'Login failed' }));
      throw err;
    }
  }, []);

  const register = useCallback(async (credentials: RegisterCredentials) => {
    setState((s) => ({ ...s, isLoading: true, error: null }));
    try {
      const session = await authRegister(credentials);
      setState({ session, isLoading: false, error: null });
      startAutoRefresh();
    } catch (err: unknown) {
      setState((s) => ({ ...s, isLoading: false, error: err instanceof Error ? err.message : 'Registration failed' }));
      throw err;
    }
  }, []);

  const loginWithOAuth = useCallback(async (provider: OAuthProvider, token: string) => {
    setState((s) => ({ ...s, isLoading: true, error: null }));
    try {
      const session = await authLoginWithOAuth(provider, token);
      setState({ session, isLoading: false, error: null });
      startAutoRefresh();
    } catch (err: unknown) {
      setState((s) => ({ ...s, isLoading: false, error: err instanceof Error ? err.message : 'OAuth login failed' }));
      throw err;
    }
  }, []);

  const loginAsGuest = useCallback(async () => {
    setState((s) => ({ ...s, isLoading: true, error: null }));
    try {
      const session = await authLoginAsGuest();
      setState({ session, isLoading: false, error: null });
      startAutoRefresh();
    } catch (err: unknown) {
      setState((s) => ({ ...s, isLoading: false, error: err instanceof Error ? err.message : 'Guest login failed' }));
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    setState((s) => ({ ...s, isLoading: true, error: null }));
    await authLogout();
    setState({ session: null, isLoading: false, error: null });
  }, []);

  const value: AuthContextValue = {
    user: state.session?.user ?? null,
    session: state.session,
    isAuthenticated: state.session !== null,
    isLoading: state.isLoading,
    error: state.error,
    login,
    register,
    loginWithOAuth,
    loginAsGuest,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useRequireAuth(): AuthContextValue & { redirectPath: string | null } {
  const auth = useAuth();
  return {
    ...auth,
    redirectPath: auth.isAuthenticated ? null : '/login',
  };
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalAuth(): { user: User | null } {
  const context = useContext(AuthContext);
  return { user: context?.user ?? null };
}
